import { useEffect, useState } from "react";
import { Loader2, PhoneCall } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, formatDate } from "@/lib/api";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

export const CALL_OUTCOMES = {
  richiamare: { l: "Da richiamare", cls: "bg-amber-100 text-amber-900 border-amber-300" },
  rinnova: { l: "Rinnova", cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  disdice: { l: "Disdice", cls: "bg-red-100 text-red-800 border-red-300" },
  non_risponde: { l: "Non risponde", cls: "bg-slate-100 text-slate-700 border-slate-300" },
  preventivo: { l: "Vuole un preventivo", cls: "bg-blue-100 text-blue-800 border-blue-300" },
};

const History = ({ logs }) => (
  <div className="max-h-48 overflow-y-auto space-y-1.5 border-t pt-3" data-testid="call-outcome-history">
    <div className="text-[11px] uppercase tracking-wide font-semibold text-slate-500">Storico telefonate</div>
    {logs.length === 0 ? <div className="text-xs text-slate-400">Nessuna telefonata registrata.</div> : logs.map((g) => (
      <div key={g.id} className="text-xs flex flex-wrap items-baseline gap-x-2" data-testid={`call-log-${g.id}`}>
        <span className="tabular-nums text-slate-500">{formatDate(g.at)}</span>
        <span className={`px-1.5 rounded border font-semibold ${CALL_OUTCOMES[g.outcome]?.cls || ""}`}>{CALL_OUTCOMES[g.outcome]?.l || g.outcome}</span>
        {g.callback_date && <span className="text-amber-800">richiamare il {formatDate(g.callback_date)}</span>}
        <span className="text-slate-400">· {g.by}</span>
        {g.note && <div className="w-full text-slate-700 pl-1">{g.note}</div>}
      </div>
    ))}
  </div>
);

export const CallOutcomeDialog = ({ row, onClose, onSaved }) => {
  const [logs, setLogs] = useState(null);
  const [outcome, setOutcome] = useState("");
  const [note, setNote] = useState("");
  const [cb, setCb] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!row) return;
    setLogs(null); setOutcome(""); setNote(""); setCb("");
    api.get(`/policies/${row.id}/call-logs`).then(({ data }) => setLogs(data)).catch((e) => { toast.error(formatApiError(e)); setLogs([]); });
  }, [row]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/policies/${row.id}/call-logs`, { outcome, note: note || null, callback_date: outcome === "richiamare" ? cb : null });
      toast.success(outcome === "disdice" ? "Esito salvato: polizza messa in stato disdetta" : "Esito telefonata salvato");
      onSaved(row.id, data.log, data.status);
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!row} onOpenChange={(o) => { if (!o && !saving) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="call-outcome-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><PhoneCall className="h-5 w-5 text-slate-700" /> Esito telefonata</DialogTitle>
        </DialogHeader>
        {row && <div className="text-sm"><b>{row.client_name}</b> <span className="text-slate-500">· {row.policy_number || "polizza"} · scad. {formatDate(row.expiry_date)}</span></div>}
        <div className="flex flex-wrap gap-1.5">
          {Object.entries(CALL_OUTCOMES).map(([k, o]) => (
            <button key={k} type="button" onClick={() => setOutcome(k)} data-testid={`call-outcome-${k}`}
              className={`h-8 px-3 rounded-full text-xs font-semibold border transition-colors ${outcome === k ? "bg-slate-900 text-white border-slate-900" : `${o.cls} hover:opacity-80`}`}>
              {o.l}
            </button>
          ))}
        </div>
        {outcome === "richiamare" && (
          <label className="text-xs flex items-center gap-2">Richiamare il
            <Input type="date" value={cb} onChange={(e) => setCb(e.target.value)} className="h-8 w-44" data-testid="call-outcome-callback-date" />
          </label>
        )}
        {outcome === "disdice" && <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2" data-testid="call-outcome-disdice-warning">La polizza verrà messa in stato <b>disdetta</b>: uscirà dallo Scadenziario e dagli avvisi automatici.</div>}
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="Nota (facoltativa)" className="text-sm" data-testid="call-outcome-note" />
        {logs ? <History logs={logs} /> : <div className="text-center py-3"><Loader2 className="h-5 w-5 animate-spin inline text-slate-400" /></div>}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="call-outcome-cancel">Chiudi</Button>
          <Button onClick={save} disabled={!outcome || (outcome === "richiamare" && !cb) || saving} data-testid="call-outcome-save"
            className={outcome === "disdice" ? "bg-red-600 hover:bg-red-700" : ""}>
            {saving && <Loader2 className="h-4 w-4 animate-spin mr-1" />} Salva esito
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
